import { Table } from '@octano/global-ui';
import { useCallback, useContext, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Input, Row } from 'reactstrap';

import { SectionTitle } from '../../../../../components/text';
import { TranslationsKeys } from '../../../../../locales/translations';
import { ModalityCodesEnum } from '../../../../SectionsMaintainer/enums/modality-codes.enum';
import { CourseFormContext } from '../../providers/CourseProvider';
import { CourseFormFields, CourseFormMode } from '../../types';

const CourseAttendanceSection = () => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);

  const { watch, setValue } = useFormContext<CourseFormFields>();

  const { mode, formData } = useContext(CourseFormContext);
  const { activities = [], modalities = [] } = formData;

  const [attendance, modality, onlineAttendancePercentage] = watch([
    'attendance',
    'modality',
    'onlineAttendancePercentage',
  ]);

  const validActivityTypes = useMemo(
    () =>
      activities?.filter((e) =>
        attendance?.map((a) => a.activityTypeId).includes(+e.value),
      ) ?? [],
    [activities, attendance],
  );

  const selectedModality = useMemo(() => {
    if (!modality?.value) return null;
    const modalityData = modalities.find((m) => m.value === modality.value);
    return modalityData;
  }, [modality, modalities]);

  const showOnlineComponent = useMemo(() => {
    // @ts-ignore - deliveryMode exists in the modality data from API
    const deliveryMode = selectedModality?.deliveryMode;
    return (
      deliveryMode === ModalityCodesEnum.OL ||
      deliveryMode === ModalityCodesEnum.SP
    );
  }, [selectedModality]);

  const onChangeAttendance = useCallback(
    (activityTypeId: number, value: string) => {
      if (isNaN(+value)) {
        return;
      }
      if (+value < 0 || +value > 100) {
        return;
      }
      const nextAttendance = [...attendance];
      const index = nextAttendance.findIndex(
        (att) => att.activityTypeId === activityTypeId,
      );
      if (index >= 0) {
        nextAttendance[index].minPercentage = +value;
      }
      setValue('attendance', nextAttendance);
    },
    [attendance, setValue],
  );

  const onChangeOnlineAttendance = useCallback(
    (value: string) => {
      if (Number.isNaN(+value)) {
        return;
      }
      if (+value < 0 || +value > 100) {
        return;
      }
      setValue('onlineAttendancePercentage', +value);
    },
    [setValue],
  );

  if (!activities?.length) {
    return <></>;
  }

  return (
    <Row>
      <Col xs={12} className="pt-4">
        <SectionTitle text={t(`assistanceRequired`)} />
      </Col>
      {mode !== CourseFormMode.WATCH && (
        <Col xs={12}>
          <p className="fs-16 fw-300">{t(`assistanceRequiredSubtitle`)}</p>
        </Col>
      )}
      <Col xs={12}>
        <Table
          columns={[
            {
              columnName: 'label',
              width: '50%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`activityType`),
            },
            {
              columnName: 'id',
              width: '50%',
              thClassName: 'text-center',
              tdClassName: 'text-center',
              headerText: t(`obligatoryAttendance`),
              cellFormat: ({ row }) => (
                <div
                  className="d-flex m-auto align-items-center"
                  style={{ maxWidth: '65px' }}
                >
                  <Input
                    name={`attendance_${row.label}`}
                    type="text"
                    onChange={(e) => {
                      onChangeAttendance(+row.value, e.target.value);
                    }}
                    value={
                      attendance.find((att) => att.activityTypeId === row.value)
                        ?.minPercentage ?? 0
                    }
                    disabled={mode === CourseFormMode.WATCH}
                  />
                  <span className="pl-2">%</span>
                </div>
              ),
            },
          ]}
          data={validActivityTypes}
        />
      </Col>
      {showOnlineComponent && (
        <Col xs={12} className="mt-3">
          <Table
            columns={[
              {
                columnName: 'label',
                width: '50%',
                thClassName: 'text-center',
                tdClassName: 'text-center',
                headerText: t(`activityType`),
              },
              {
                columnName: 'id',
                width: '50%',
                thClassName: 'text-center',
                tdClassName: 'text-center',
                headerText: t(`obligatoryAttendance`),
                cellFormat: () => (
                  <div
                    className="d-flex m-auto align-items-center"
                    style={{ maxWidth: '65px' }}
                  >
                    <Input
                      name="onlineAttendancePercentage"
                      type="text"
                      onChange={(e) => {
                        onChangeOnlineAttendance(e.target.value);
                      }}
                      value={onlineAttendancePercentage ?? 0}
                      disabled={mode === CourseFormMode.WATCH}
                    />
                    <span className="pl-2">%</span>
                  </div>
                ),
              },
            ]}
            data={[{ label: t('onlineComponent'), id: 'online' }]}
          />
        </Col>
      )}
    </Row>
  );
};

export default CourseAttendanceSection;
